import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import axios from "axios";
import { format, isToday, isYesterday, isThisWeek, parseISO } from "date-fns";
import { useNavigate, useLocation } from "react-router-dom";
import { formatScheduledTime, extractTimeFromDate } from "../../utils/dateUtils";
import {
  MessageSquare, Search, Send, ArrowLeft, Phone, User, Clock,
  MapPin, Briefcase, ChevronRight, Loader2, MessageCircle,
  CheckCheck, Check, AlertCircle, RefreshCw, Plus, X, Users,
  Pin, PinOff, Archive, ArchiveRestore, Eye, EyeOff,
  MoreVertical, PhoneCall, Copy, UserPlus, Sparkles, RotateCcw,
  Wrench, ChevronDown, ExternalLink, ArrowDown, Bot,
  Image as ImageIcon, Download, ZoomIn
} from "lucide-react";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from "../../components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger
} from "../../components/ui/dropdown-menu";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger
} from "../../components/ui/tooltip";
import { toast } from "sonner";
import { API } from "../../App";
import { listenForPushMessages } from "../../utils/notificationSound";
import ThumbtackConversationPane from "../../components/messages/ThumbtackConversationPane";

const getAuth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

/* ═══ HELPERS ═══ */
const fmtTs = (ts) => {
  if (!ts) return "";
  try { const d = parseISO(ts); if (isToday(d)) return format(d, "h:mm a"); if (isYesterday(d)) return "Yesterday"; if (isThisWeek(d)) return format(d, "EEE"); return format(d, "MMM d"); } catch { return ""; }
};
const fmtFull = (ts) => {
  if (!ts) return "";
  try { const d = parseISO(ts); if (isToday(d)) return format(d, "h:mm a"); if (isYesterday(d)) return `Yesterday ${format(d, "h:mm a")}`; return format(d, "MMM d, h:mm a"); } catch { return ""; }
};
const showDateSep = (curr, prev) => {
  if (!prev) return true;
  try { return parseISO(curr).toDateString() !== parseISO(prev).toDateString(); } catch { return false; }
};
const fmtDateSep = (ts) => {
  try { const d = parseISO(ts); if (isToday(d)) return "Today"; if (isYesterday(d)) return "Yesterday"; return format(d, "EEEE, MMMM d"); } catch { return ""; }
};

const statusColors = {
  scheduled: "bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/30",
  in_progress: "bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30",
  completed: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30",
  completed_pending_payment: "bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30",
  deposit_collected: "bg-teal-500/15 text-teal-600 dark:text-teal-400 border-teal-500/30",
  pending_approval: "bg-purple-500/15 text-purple-600 dark:text-purple-400 border-purple-500/30",
  cancelled: "bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/30",
  invoiced: "bg-cyan-500/15 text-cyan-600 dark:text-cyan-400 border-cyan-500/30",
};

/* ═══ CONVERSATION ITEM ═══ */
const ConversationItem = React.memo(({ conv, isActive, isHighlighted, onClick, onPin, onArchive, onMarkUnread }) => {
  const unread = conv.unread_count > 0;
  const isTt = conv.channel === "thumbtack";
  return (
    <div className={`group relative transition-all duration-500 ${isHighlighted ? "conv-highlight" : ""}`} data-testid={`conversation-${conv.conversation_id}`}>
      <button onClick={onClick} className={`w-full text-left px-3 py-3 flex items-center gap-3 transition-all border-b border-border/20 hover:bg-accent/50 ${isActive ? "bg-accent" : ""}`}>
        <div className="relative flex-shrink-0">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isTt ? "bg-[#009fd9]/15 text-[#009fd9]" : conv.contact_type === "technician" ? "bg-amber-500/15 text-amber-500" : "bg-blue-500/15 text-blue-500"}`}>
            {isTt ? <MessageCircle className="w-4.5 h-4.5" /> : <User className="w-4.5 h-4.5" />}
          </div>
          {conv.is_pinned && <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-primary/90 flex items-center justify-center"><Pin className="w-2.5 h-2.5 text-primary-foreground" /></div>}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <span className={`flex items-center gap-1.5 min-w-0 text-sm ${unread ? "font-semibold text-foreground" : "font-medium text-foreground/80"}`}>
              <span className="truncate">{conv.contact_name || conv.contact_number || "Unknown"}</span>
              {isTt && <span className="text-[9px] font-semibold uppercase tracking-wide text-[#009fd9] bg-[#009fd9]/10 border border-[#009fd9]/25 rounded px-1 py-px flex-shrink-0" data-testid={`tt-badge-${conv.conversation_id}`}>Thumbtack</span>}
            </span>
            <span className={`text-[10px] flex-shrink-0 ${unread ? "text-primary font-semibold" : "text-muted-foreground"}`}>{fmtTs(conv.last_message_at)}</span>
          </div>
          <div className="flex items-center justify-between gap-2 mt-0.5">
            <p className={`text-xs truncate max-w-[180px] ${unread ? "text-foreground/70 font-medium" : "text-muted-foreground"}`}>{conv.last_message || "No messages yet"}</p>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              {isTt && conv.job_display_id && <span className="flex items-center gap-0.5 text-[9px] font-medium bg-[#009fd9]/10 text-[#009fd9] px-1.5 py-0.5 rounded-full"><Briefcase className="w-2.5 h-2.5" />{conv.job_display_id}</span>}
              {conv.active_job_count > 0 && <span className="flex items-center gap-0.5 text-[9px] font-medium bg-primary/10 text-primary px-1.5 py-0.5 rounded-full"><Briefcase className="w-2.5 h-2.5" />{conv.active_job_count}</span>}
              {unread && <span className={`w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center ${isTt ? "bg-[#009fd9] text-white" : "bg-primary text-primary-foreground"}`}>{conv.unread_count > 9 ? "9+" : conv.unread_count}</span>}
            </div>
          </div>
        </div>
      </button>
      {!isTt && (
      <div className="absolute right-2 top-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <DropdownMenu>
          <DropdownMenuTrigger asChild><button className="p-1 rounded-md hover:bg-muted/60" data-testid={`conv-menu-${conv.conversation_id}`}><MoreVertical className="w-3.5 h-3.5 text-muted-foreground" /></button></DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuItem onClick={e => { e.stopPropagation(); onPin(); }}>{conv.is_pinned ? <PinOff className="w-3.5 h-3.5 mr-2" /> : <Pin className="w-3.5 h-3.5 mr-2" />}{conv.is_pinned ? "Unpin" : "Pin to top"}</DropdownMenuItem>
            <DropdownMenuItem onClick={e => { e.stopPropagation(); onMarkUnread(); }}><EyeOff className="w-3.5 h-3.5 mr-2" /> Mark as unread</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={e => { e.stopPropagation(); onArchive(); }} className="text-muted-foreground">{conv.is_archived ? <ArchiveRestore className="w-3.5 h-3.5 mr-2" /> : <Archive className="w-3.5 h-3.5 mr-2" />}{conv.is_archived ? "Unarchive" : "Archive"}</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      )}
    </div>
  );
});

/* ═══ MESSAGE SEARCH RESULT ═══ */
const highlightSnippet = (body, query) => {
  const b = body || "";
  const i = b.toLowerCase().indexOf((query || "").toLowerCase());
  if (i === -1 || !query) return b.slice(0, 90);
  const start = Math.max(0, i - 28);
  const pre = (start > 0 ? "…" : "") + b.slice(start, i);
  const end = i + query.length;
  const post = b.slice(end, end + 60) + (end + 60 < b.length ? "…" : "");
  return (<>{pre}<mark className="bg-primary/25 text-foreground rounded-sm px-0.5">{b.slice(i, end)}</mark>{post}</>);
};

const MessageSearchResult = React.memo(({ result, query, onClick }) => (
  <button onClick={onClick} className="w-full text-left px-3 py-2.5 flex items-start gap-3 border-b border-border/20 hover:bg-accent/50 transition-colors" data-testid={`msg-search-result-${result.conversation_id}`}>
    <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${result.contact_type === "technician" ? "bg-amber-500/15 text-amber-500" : "bg-blue-500/15 text-blue-500"}`}>
      <MessageCircle className="w-4 h-4" />
    </div>
    <div className="min-w-0 flex-1">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-foreground/90 truncate">{result.contact_name || fmtPhoneDisplay(result.contact_number) || "Unknown"}</span>
        <span className="text-[10px] text-muted-foreground flex-shrink-0">{fmtTs(result.message?.created_at)}</span>
      </div>
      <p className="text-xs text-muted-foreground mt-0.5 break-words">
        {result.message?.direction === "outbound" && <span className="text-foreground/60 font-medium">You: </span>}
        {highlightSnippet(result.message?.body, query)}
      </p>
      <div className="flex items-center gap-1 mt-1">
        {result.match_count > 1 && <span className="text-[9px] font-medium bg-primary/10 text-primary px-1.5 py-0.5 rounded-full">{result.match_count} matching messages</span>}
        {result.is_archived && <span className="text-[9px] font-medium bg-muted text-muted-foreground px-1.5 py-0.5 rounded-full">Archived</span>}
      </div>
    </div>
  </button>
));

/* ═══ JOB CARD ═══ */
const JobCard = React.memo(({ jobData, onJobClick }) => {
  if (!jobData) return null;
  const sc = statusColors[jobData.status] || statusColors.scheduled;
  return (
    <div onClick={() => onJobClick?.(jobData.job_id)} className="bg-card border border-border rounded-xl p-3 cursor-pointer hover:border-primary/40 hover:shadow-md transition-all group/card max-w-[calc(100vw-6rem)] sm:max-w-[320px]" data-testid={`job-card-${jobData.job_id}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2"><div className="w-7 h-7 rounded-lg bg-primary/15 flex items-center justify-center"><Briefcase className="w-3.5 h-3.5 text-primary" /></div><div><p className="text-xs font-semibold text-foreground leading-tight">{jobData.title || "Service Job"}</p><p className="text-[10px] text-muted-foreground font-mono">{jobData.job_display_id || jobData.job_id}</p></div></div>
        <ChevronRight className="w-4 h-4 text-muted-foreground group-hover/card:text-foreground transition-colors" />
      </div>
      <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border mb-2 ${sc}`}>{(jobData.status || "scheduled").replace(/_/g, " ")}</div>
      {jobData.customer_address && <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground mb-1"><MapPin className="w-3 h-3 flex-shrink-0" /><span className="truncate">{jobData.customer_address}</span></div>}
      {(jobData.scheduled_date || jobData.scheduled_time) && <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground"><Clock className="w-3 h-3 flex-shrink-0" /><span>{[jobData.scheduled_date, formatScheduledTime(jobData.scheduled_time) || extractTimeFromDate(jobData.scheduled_date)].filter(Boolean).join(" at ")}</span></div>}
    </div>
  );
});

/* ═══ MESSAGE BUBBLE ═══ */
/* ═══ MEDIA LIGHTBOX ═══ */
const MediaLightbox = ({ url, contentType, onClose }) => {
  if (!url) return null;
  const isImage = (contentType || "").startsWith("image/") || /\.(jpg|jpeg|png|gif|webp|heic)/i.test(url);

  const handleDownload = async () => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `media_${Date.now()}.${isImage ? "jpg" : "mp4"}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch {
      window.open(url, "_blank");
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center" onClick={onClose} data-testid="media-lightbox">
      <button onClick={onClose} className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors">
        <X className="w-6 h-6" />
      </button>
      <button onClick={(e) => { e.stopPropagation(); handleDownload(); }} className="absolute top-4 left-4 z-10 flex items-center gap-2 px-3 py-2 rounded-full bg-white/10 hover:bg-white/20 text-white text-sm transition-colors" data-testid="media-download-btn">
        <Download className="w-4 h-4" /> Save
      </button>
      <div className="max-w-[90vw] max-h-[85vh]" onClick={(e) => e.stopPropagation()}>
        {isImage ? (
          <img src={url} alt="Media" className="max-w-full max-h-[85vh] object-contain rounded-lg" />
        ) : (
          <video src={url} controls autoPlay className="max-w-full max-h-[85vh] rounded-lg" />
        )}
      </div>
    </div>
  );
};

/* ═══ MESSAGE MEDIA RENDERER ═══ */
const MessageMedia = ({ media, isOut }) => {
  const [lightbox, setLightbox] = useState(null);
  if (!media || media.length === 0) return null;

  return (
    <>
      <div className={`flex flex-wrap gap-1.5 mb-1 ${media.length === 1 ? "" : "grid grid-cols-2"}`}>
        {media.map((m, i) => {
          const isImage = (m.content_type || "").startsWith("image/") || /\.(jpg|jpeg|png|gif|webp|heic)/i.test(m.url);
          const isVideo = (m.content_type || "").startsWith("video/");
          return (
            <button
              key={i}
              onClick={() => setLightbox(m)}
              className={`relative group/media overflow-hidden rounded-xl ${media.length === 1 ? "max-w-[280px]" : "aspect-square"} bg-muted/30 border border-border/20 hover:border-primary/30 transition-all`}
              data-testid={`media-thumb-${i}`}
            >
              {isImage ? (
                <img src={m.url} alt="" loading="lazy" className={`${media.length === 1 ? "w-full max-h-[300px]" : "w-full h-full"} object-cover`} />
              ) : isVideo ? (
                <video src={m.url} className={`${media.length === 1 ? "w-full max-h-[300px]" : "w-full h-full"} object-cover`} />
              ) : (
                <div className="flex items-center justify-center p-4 gap-2">
                  <Download className="w-5 h-5 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">Attachment</span>
                </div>
              )}
              <div className="absolute inset-0 bg-black/0 group-hover/media:bg-black/20 transition-colors flex items-center justify-center">
                <ZoomIn className="w-6 h-6 text-white opacity-0 group-hover/media:opacity-100 transition-opacity drop-shadow-lg" />
              </div>
            </button>
          );
        })}
      </div>
      {lightbox && <MediaLightbox url={lightbox.url} contentType={lightbox.content_type} onClose={() => setLightbox(null)} />}
    </>
  );
};

/* ═══ MESSAGE BUBBLE ═══ */
const MessageBubble = React.memo(({ msg, onJobClick, onRetry, highlight }) => {
  const isOut = msg.direction === "outbound";
  const isJobCard = msg.message_type === "job_assignment" && msg.job_data;
  const isFailed = msg.status === "failed" || msg.status === "undelivered";
  const isSending = msg._optimistic;
  const isAuto = !!(msg.automation_id || msg.template_id || msg.template_name);
  const hasMedia = msg.media && msg.media.length > 0;
  const hasBody = msg.body && msg.body.trim();
  return (
    <div className={`flex ${isOut ? "justify-end" : "justify-start"} mb-1 group/msg ${highlight ? "msg-jump-highlight" : ""}`} data-msg-id={msg.message_id}>
      <div className={`max-w-[80%] sm:max-w-[70%] flex flex-col ${isOut ? "items-end" : "items-start"}`}>
        {isAuto && isOut && <div className="flex items-center gap-1 mb-0.5 px-1"><Bot className="w-3 h-3 text-muted-foreground/50" /><span className="text-[9px] text-muted-foreground/50 font-medium">{msg.template_name || "Automation"}</span></div>}
        {isJobCard && <div className="mb-1"><JobCard jobData={msg.job_data} onJobClick={onJobClick} /></div>}
        {hasMedia && <MessageMedia media={msg.media} isOut={isOut} />}
        {hasBody && (
          <div className={`relative px-3 py-2 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap break-words overflow-hidden ${isOut ? `rounded-br-md ${isFailed ? "bg-red-500/80 text-white" : isSending ? "bg-primary/60 text-primary-foreground" : "bg-primary text-primary-foreground"}` : "bg-muted/60 text-foreground rounded-bl-md"}`} style={{ overflowWrap: "anywhere" }}>{msg.body}</div>
        )}
        {!hasBody && !hasMedia && !isJobCard && (
          <div className={`relative px-3 py-2 rounded-2xl text-sm leading-relaxed ${isOut ? "bg-primary text-primary-foreground rounded-br-md" : "bg-muted/60 text-foreground rounded-bl-md"}`}>
            <span className="italic text-muted-foreground/70">Empty message</span>
          </div>
        )}
        <div className={`flex items-center gap-1 mt-0.5 px-1 ${isOut ? "justify-end" : ""}`}>
          {hasMedia && !hasBody && <ImageIcon className="w-3 h-3 text-muted-foreground/50" />}
          <span className={`text-[10px] ${isSending ? "text-muted-foreground/40" : "text-muted-foreground/60"}`}>{isSending ? "Sending..." : fmtFull(msg.created_at)}</span>
          {/* Show which of OUR numbers received this inbound message — useful
              when an account owns multiple Twilio numbers. Only on inbound. */}
          {!isOut && msg.to_number && (
            <span
              className="text-[10px] text-muted-foreground/50 font-mono"
              title="Number this message was sent to"
              data-testid={`msg-to-number-${msg.message_id || msg.message_sid || ""}`}
            >
              · To: {msg.to_number}
            </span>
          )}
          {isOut && !isSending && (isFailed ? <button onClick={() => onRetry?.(msg.message_id)} className="flex items-center gap-0.5 text-[10px] text-red-400 hover:text-red-300 transition-colors" data-testid={`retry-${msg.message_id}`}><RotateCcw className="w-3 h-3" /> Retry</button> : msg.status === "delivered" ? <CheckCheck className="w-3 h-3 text-blue-400" /> : (msg.status === "sent" || msg.status === "queued") ? <Check className="w-3 h-3 text-muted-foreground/50" /> : null)}
        </div>
      </div>
    </div>
  );
});

/* ═══ SMALL COMPONENTS ═══ */
const DateSeparator = ({ date }) => <div className="flex items-center justify-center my-3"><div className="bg-muted/40 text-muted-foreground text-[10px] font-medium px-3 py-1 rounded-full">{date}</div></div>;

const EmptyInbox = () => (
  <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
    <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-4"><MessageCircle className="w-8 h-8 text-primary/60" /></div>
    <h3 className="text-lg font-semibold text-foreground mb-1">No Conversations</h3>
    <p className="text-sm text-muted-foreground max-w-[280px]">Messages will appear here when you communicate with customers or technicians</p>
  </div>
);

const NoChatSelected = () => (
  <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
    <div className="w-20 h-20 rounded-2xl bg-muted/30 flex items-center justify-center mb-4"><MessageSquare className="w-10 h-10 text-muted-foreground/40" /></div>
    <h3 className="text-lg font-semibold text-foreground mb-1">Select a conversation</h3>
    <p className="text-sm text-muted-foreground">Choose a thread from the left to start messaging</p>
  </div>
);

/* ═══ AI REPLY CHIPS ═══ */
const AiReplyChips = React.memo(({ conversationId, onSelect }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const fetchSuggestions = useCallback(async () => {
    if (!conversationId) return; setLoading(true);
    try { const res = await axios.post(`${API}/sms/ai/suggest-replies`, { conversation_id: conversationId }, getAuth()); setSuggestions(res.data?.suggestions || []); setVisible(true); }
    catch { toast.error("Couldn't generate suggestions"); } finally { setLoading(false); }
  }, [conversationId]);
  if (!visible && !loading) return <button onClick={fetchSuggestions} className="flex items-center gap-1.5 text-[11px] text-primary/70 hover:text-primary transition-colors px-2 py-1 rounded-lg hover:bg-primary/5" data-testid="ai-suggest-btn"><Sparkles className="w-3.5 h-3.5" /> AI Suggestions</button>;
  if (loading) return <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground px-2 py-1"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Generating...</div>;
  return (
    <div className="flex items-center gap-1.5 overflow-x-auto pb-0.5" data-testid="ai-suggestions-container"><Sparkles className="w-3.5 h-3.5 text-primary/60 flex-shrink-0" />
      {suggestions.map((s, i) => <button key={i} onClick={() => { onSelect(s); setVisible(false); setSuggestions([]); }} className="flex-shrink-0 text-[11px] px-2.5 py-1 rounded-full bg-primary/8 text-primary border border-primary/15 hover:bg-primary/15 transition-colors whitespace-nowrap" data-testid={`ai-chip-${i}`}>{s.length > 60 ? s.slice(0, 60) + "..." : s}</button>)}
      <button onClick={() => { setVisible(false); setSuggestions([]); }} className="p-0.5 text-muted-foreground hover:text-foreground"><X className="w-3 h-3" /></button>
    </div>
  );
});

/* ═══ LINKED JOBS PANEL ═══ */
const LinkedJobsPanel = ({ jobs, onJobClick }) => {
  const [expanded, setExpanded] = useState(false);
  if (!jobs || jobs.length === 0) return null;
  return (
    <div className="border-b border-border/30">
      <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center justify-between px-3 py-2 hover:bg-muted/30 transition-colors" data-testid="linked-jobs-toggle">
        <span className="flex items-center gap-1.5 text-xs font-medium text-foreground"><Briefcase className="w-3.5 h-3.5 text-primary" />{jobs.length} Linked Job{jobs.length !== 1 ? "s" : ""}</span>
        <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${expanded ? "rotate-180" : ""}`} />
      </button>
      {expanded && <div className="px-3 pb-2 space-y-1.5">{jobs.map(j => <button key={j.job_id} onClick={() => onJobClick(j.job_id)} className="w-full text-left flex items-center gap-2 p-2 rounded-lg bg-muted/20 hover:bg-muted/40 transition-colors" data-testid={`linked-job-${j.job_id}`}><div className={`w-2 h-2 rounded-full flex-shrink-0 ${j.status === "completed" ? "bg-emerald-500" : j.status === "in_progress" ? "bg-amber-500" : "bg-blue-500"}`} /><div className="min-w-0 flex-1"><p className="text-[11px] font-medium text-foreground truncate">{j.title || j.service_name || "Job"}</p><p className="text-[10px] text-muted-foreground">{j.job_display_id} - {(j.status || "").replace(/_/g, " ")}</p></div><ExternalLink className="w-3 h-3 text-muted-foreground" /></button>)}</div>}
    </div>
  );
};

/* ═══ NEW CONVERSATION DIALOG ═══ */
const fmtPhoneDisplay = (raw) => {
  const digits = (raw || "").replace(/\D/g, "");
  const d10 = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  if (d10.length === 10) return `(${d10.slice(0, 3)}) ${d10.slice(3, 6)}-${d10.slice(6)}`;
  return raw;
};

const NewConversationDialog = ({ open, onClose, onSelect }) => {
  const [contacts, setContacts] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("all");
  useEffect(() => {
    if (!open) return; setLoading(true);
    Promise.all([
      axios.get(`${API}/customers`, getAuth()).catch(() => ({ data: [] })),
      axios.get(`${API}/technicians`, getAuth()).catch(() => ({ data: [] })),
    ]).then(([cr, tr]) => {
      const rawCusts = Array.isArray(cr.data) ? cr.data : cr.data?.customers || [];
      // A customer with primary + secondary phone gets one row per number so
      // the user picks the EXACT destination number (kept until sent/changed).
      const custs = rawCusts.filter(c => c.phone || c.secondary_phone).flatMap(c => {
        const rows = [];
        if (c.phone) rows.push({ id: c.customer_id, name: c.name, phone: c.phone, type: "customer", phoneLabel: c.secondary_phone ? "Primary" : null });
        if (c.secondary_phone) rows.push({ id: c.customer_id, name: c.name, phone: c.secondary_phone, type: "customer", phoneLabel: "Secondary" });
        return rows;
      });
      const techs = (Array.isArray(tr.data) ? tr.data : tr.data?.technicians || []).filter(t => t.phone).map(t => ({ id: t.technician_id, name: t.name, phone: t.phone, type: "technician" }));
      setContacts([...custs, ...techs]);
    }).finally(() => setLoading(false));
  }, [open]);
  const filtered = useMemo(() => {
    let list = contacts;
    if (tab !== "all") list = list.filter(c => c.type === tab);
    if (searchQuery) { const q = searchQuery.toLowerCase(); list = list.filter(c => c.name?.toLowerCase().includes(q) || c.phone?.includes(q)); }
    return list;
  }, [contacts, tab, searchQuery]);
  const customNumber = useMemo(() => {
    const digits = searchQuery.replace(/\D/g, "");
    if (digits.length === 10) return `+1${digits}`;
    if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
    if (digits.length >= 11 && searchQuery.trim().startsWith("+")) return `+${digits}`;
    return null;
  }, [searchQuery]);
  const customIsExistingContact = useMemo(() => {
    if (!customNumber) return false;
    const last10 = customNumber.replace(/\D/g, "").slice(-10);
    return contacts.some(c => (c.phone || "").replace(/\D/g, "").slice(-10) === last10);
  }, [customNumber, contacts]);
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md max-h-[80vh] flex flex-col p-0">
        <DialogHeader className="px-4 pt-4 pb-2"><DialogTitle className="text-base">New Conversation</DialogTitle></DialogHeader>
        <div className="px-4 pb-2 space-y-2">
          <div className="relative"><Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" /><Input placeholder="Search contacts or enter a phone number..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-8 h-9 text-sm" data-testid="new-conv-search" autoFocus /></div>
          <div className="flex gap-1">{["all", "customer", "technician"].map(t => <button key={t} onClick={() => setTab(t)} className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors ${tab === t ? "bg-primary text-primary-foreground" : "bg-muted/40 text-muted-foreground hover:bg-muted/60"}`} data-testid={`new-conv-filter-${t}`}>{t === "all" ? "All" : t === "customer" ? "Customers" : "Technicians"}</button>)}</div>
        </div>
        <div className="flex-1 overflow-y-auto min-h-0 border-t border-border/40">
          {customNumber && !customIsExistingContact && (
            <button onClick={() => { onSelect({ id: null, name: null, phone: customNumber, type: null, isCustom: true }); onClose(false); }} className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-accent/50 transition-colors border-b border-border/20 bg-primary/5" data-testid="new-conv-custom-number">
              <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 bg-emerald-500/15 text-emerald-500"><Phone className="w-4 h-4" /></div>
              <div className="min-w-0 flex-1"><p className="text-sm font-medium text-foreground">Text {fmtPhoneDisplay(customNumber)}</p><p className="text-xs text-muted-foreground">Start a conversation with this number</p></div>
              <Badge variant="outline" className="text-[10px] border-emerald-500/30 text-emerald-500">New number</Badge>
            </button>
          )}
          {loading ? <div className="flex items-center justify-center p-8"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div> :
          filtered.length === 0 ? (customNumber ? null : <div className="flex flex-col items-center justify-center p-8 text-center"><Users className="w-8 h-8 text-muted-foreground/40 mb-2" /><p className="text-sm text-muted-foreground">No contacts found</p><p className="text-[11px] text-muted-foreground/60 mt-1">Tip: type or paste a full phone number to text it directly</p></div>) :
          filtered.map(c => (
            <button key={`${c.type}-${c.id}-${c.phone}`} onClick={() => { onSelect(c); onClose(false); }} className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-accent/50 transition-colors border-b border-border/20" data-testid={`new-conv-contact-${c.id}${c.phoneLabel === "Secondary" ? "-secondary" : ""}`}>
              <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${c.type === "technician" ? "bg-amber-500/15 text-amber-500" : "bg-blue-500/15 text-blue-500"}`}><User className="w-4 h-4" /></div>
              <div className="min-w-0 flex-1"><p className="text-sm font-medium text-foreground truncate">{c.name}</p><p className="text-xs text-muted-foreground">{c.phone}{c.phoneLabel && <span className="ml-1.5 text-[10px] text-muted-foreground/70">({c.phoneLabel})</span>}</p></div>
              <Badge variant="outline" className={`text-[10px] ${c.type === "technician" ? "border-amber-500/30 text-amber-500" : "border-blue-500/30 text-blue-500"}`}>{c.type === "technician" ? "Tech" : "Customer"}</Badge>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* ═══════════════════════════════════════════
   MAIN MESSAGES PAGE
   ═══════════════════════════════════════════ */
const MessagesPage = () => {
  /* ── state ── */
  const [conversations, setConversations] = useState([]);
  const [filteredConvs, setFilteredConvs] = useState([]);
  const [activeConvId, setActiveConvId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [showArchived, setShowArchived] = useState(false);
  // Dashboard deep-link (?filter=unread) — show only conversations with unread messages
  const [unreadOnly, setUnreadOnly] = useState(() => new URLSearchParams(window.location.search).get("filter") === "unread");
  const [loading, setLoading] = useState(true);
  // Server-side search inside message bodies
  const [msgResults, setMsgResults] = useState([]);
  const [msgSearching, setMsgSearching] = useState(false);
  const msgSearchSeqRef = useRef(0);
  // Jump-to-message: target set when a search result is clicked; the chat
  // auto-loads older pages until the message is present, then scrolls to it.
  const jumpTargetRef = useRef(null); // { convId, messageId, attempts }
  const [jumpTick, setJumpTick] = useState(0);
  const [jumpHighlightId, setJumpHighlightId] = useState(null);

  const [messages, setMessages] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [oldestCursor, setOldestCursor] = useState(null);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);

  const [convJobs, setConvJobs] = useState([]);
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [selectedNumber, setSelectedNumber] = useState("");
  const [showChat, setShowChat] = useState(false);
  const [showNewConv, setShowNewConv] = useState(false);

  const [highlightedConvIds, setHighlightedConvIds] = useState(new Set());
  const [showNewMsgBtn, setShowNewMsgBtn] = useState(false);
  const [newMsgCount, setNewMsgCount] = useState(0);

  /* ── Thumbtack channel (additive — fully separate from the SMS system) ── */
  const [ttConvs, setTtConvs] = useState([]);
  const fetchTtConvs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/thumbtack/conversations`, getAuth());
      setTtConvs((res.data?.conversations || []).map(c => ({
        ...c,
        conversation_id: `tt_${c.negotiation_id}`,
        channel: "thumbtack",
        contact_name: c.customer_name,
      })));
    } catch { /* Thumbtack not connected / no conversations — SMS unaffected */ }
  }, []);
  useEffect(() => {
    fetchTtConvs();
    const iv = setInterval(fetchTtConvs, 10000);
    return () => clearInterval(iv);
  }, [fetchTtConvs]);

  /* ── refs ── */
  const pollRef = useRef(null);
  const inputRef = useRef(null);
  const scrollRef = useRef(null);       // scroll container
  const bottomRef = useRef(null);        // sentinel at the very bottom
  const isNearBottomRef = useRef(true);
  const convTsMapRef = useRef(new Map()); // convId -> last_message_at
  const latestMsgTsRef = useRef(null);
  const activeConvIdRef = useRef(null);
  const pollingRef = useRef(false);      // prevent concurrent polls
  // Stored recipient for a just-started (not yet sent) conversation. This is
  // the durable source of truth for the destination — list refreshes/polls
  // must never clear it. Only send-success, adoption of the real
  // conversation, or an explicit user cancel may clear it.
  const pendingRecipientRef = useRef(null);

  useEffect(() => { activeConvIdRef.current = activeConvId; }, [activeConvId]);
  const activeConv = useMemo(() => conversations.find(c => c.conversation_id === activeConvId), [conversations, activeConvId]);
  const isSearchActive = searchQuery.trim().length >= 2;
  const isTtActive = !!(activeConvId && activeConvId.startsWith("tt_"));
  const activeTtConv = useMemo(() => ttConvs.find(c => c.conversation_id === activeConvId), [ttConvs, activeConvId]);
  const navigate = useNavigate();
  const location = useLocation();

  // Deep-link: /dashboard/messages?conv={conversation_id} (e.g. tt_{negotiation_id}
  // from a "Thumbtack reply" notification) opens that exact conversation.
  useEffect(() => {
    const conv = new URLSearchParams(location.search).get("conv");
    if (!conv) return;
    setActiveConvId(conv);
    setShowChat(true);
    navigate("/dashboard/messages", { replace: true });
  }, [location.search, navigate]);
  // Customer profile link: real convs carry customer_id, temp convs carry contact_id
  const customerProfileId = activeConv?.customer_id || (activeConv?.contact_type === "customer" ? activeConv?.contact_id : null);

  /* Keep the pending (temp) new-conversation entry alive across ANY list
     refresh (poll / manual refresh / archive toggle). Without this, the
     3-second poll replaced the list and the selected recipient flipped to
     "Unknown". If the server now has a real conversation for that number
     (e.g. the contact texted in first), adopt it and keep the draft. */
  const mergeWithPendingTemp = useCallback((fresh) => {
    const pending = pendingRecipientRef.current;
    if (!pending) return fresh;
    const digits = (pending.contact_number || "").replace(/\D/g, "").slice(-10);
    const real = fresh.find(c => (c.contact_number || "").replace(/\D/g, "").slice(-10) === digits);
    if (real) {
      pendingRecipientRef.current = null;
      if (activeConvIdRef.current === pending.conversation_id) setActiveConvId(real.conversation_id);
      return fresh;
    }
    return [pending, ...fresh];
  }, []);

  /* ── scroll helpers ── */
  const scrollToBottom = useCallback((smooth = true) => {
    requestAnimationFrame(() => {
      bottomRef.current?.scrollIntoView({ behavior: smooth ? "smooth" : "instant" });
    });
    setShowNewMsgBtn(false);
    setNewMsgCount(0);
    isNearBottomRef.current = true;
  }, []);

  const checkIfNearBottom = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
    isNearBottomRef.current = dist < 120;
    if (isNearBottomRef.current && showNewMsgBtn) { setShowNewMsgBtn(false); setNewMsgCount(0); }
  }, [showNewMsgBtn]);

  /* ── data fetchers ── */
  const fetchPhoneNumbers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/settings/phone-numbers`, getAuth());
      const nums = res.data || [];
      setPhoneNumbers(nums);
      if (nums.length > 0 && !selectedNumber) { const biz = nums.find(n => n.assignment_type === "business"); setSelectedNumber(biz?.phone_number || nums[0].phone_number); }
    } catch {}
  }, [selectedNumber]);

  const fetchConvJobs = useCallback(async (convId) => {
    try { const res = await axios.get(`${API}/sms/conversations/${convId}/jobs`, getAuth()); setConvJobs(res.data?.jobs || []); }
    catch { setConvJobs([]); }
  }, []);

  /* fetch full message history (initial or load-older) */
  const fetchMessages = useCallback(async (convId, cursor = null) => {
    try {
      if (cursor) setLoadingOlder(true); else setLoadingMessages(true);
      const params = new URLSearchParams({ limit: "50" });
      if (cursor) params.set("before", cursor);
      const res = await axios.get(`${API}/sms/conversations/${convId}/messages?${params}`, getAuth());
      const data = res.data;
      const msgs = data.messages || [];
      if (cursor) {
        setMessages(prev => [...msgs, ...prev]);
      } else {
        setMessages(msgs);
        if (msgs.length > 0) latestMsgTsRef.current = msgs[msgs.length - 1].created_at;
        else latestMsgTsRef.current = null;
        // Opening a conversation marks it read server-side — refresh the
        // sidebar unread badge immediately instead of waiting for the poll.
        window.dispatchEvent(new Event("fieldz-unread-refresh"));
      }
      setHasMore(data.has_more || false);
      setOldestCursor(data.oldest_cursor || null);
    } catch { if (!cursor) toast.error("Failed to load messages"); }
    finally { setLoadingMessages(false); setLoadingOlder(false); }
  }, []);

  /* fetch only NEW messages since last known timestamp */
  const fetchNewMessages = useCallback(async (convId) => {
    const since = latestMsgTsRef.current;
    if (!since) { await fetchMessages(convId); return; } // fallback to full fetch
    try {
      const res = await axios.get(`${API}/sms/conversations/${convId}/messages?since=${encodeURIComponent(since)}`, getAuth());
      const incoming = res.data?.messages || [];
      if (incoming.length === 0) return;
      setMessages(prev => {
        const ids = new Set(prev.filter(m => !m._optimistic).map(m => m.message_id));
        const unique = incoming.filter(m => !ids.has(m.message_id));
        if (unique.length === 0) return prev;
        // Remove any remaining optimistic msgs that the server has now confirmed
        const cleaned = prev.filter(m => !m._optimistic);
        const merged = [...cleaned, ...unique];
        latestMsgTsRef.current = merged[merged.length - 1].created_at;
        if (!isNearBottomRef.current) { setShowNewMsgBtn(true); setNewMsgCount(c => c + unique.length); }
        return merged;
      });
    } catch {}
  }, [fetchMessages]);

  /* ── Conversation fetcher + change detection ── */
  const fetchConversations = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const params = showArchived ? "?include_archived=true" : "";
      const res = await axios.get(`${API}/sms/conversations${params}`, getAuth());
      const newConvs = res.data || [];

      // Detect updated conversations (only in silent/poll mode)
      if (silent) {
        const currentActive = activeConvIdRef.current;
        const highlights = new Set();
        newConvs.forEach(c => {
          const prev = convTsMapRef.current.get(c.conversation_id);
          if (prev && c.last_message_at !== prev && c.conversation_id !== currentActive) highlights.add(c.conversation_id);
        });
        if (highlights.size > 0) {
          setHighlightedConvIds(p => { const m = new Set(p); highlights.forEach(id => m.add(id)); return m; });
          setTimeout(() => setHighlightedConvIds(p => { const n = new Set(p); highlights.forEach(id => n.delete(id)); return n; }), 3000);
        }
      }

      // Cache timestamps
      const map = new Map();
      newConvs.forEach(c => map.set(c.conversation_id, c.last_message_at));
      convTsMapRef.current = map;
      setConversations(mergeWithPendingTemp(newConvs));
    } catch { if (!silent) toast.error("Failed to load conversations"); }
    finally { if (!silent) setLoading(false); }
  }, [showArchived, mergeWithPendingTemp]);

  /* ── POLL: single unified function ── */
  const poll = useCallback(async () => {
    if (pollingRef.current) return; // skip if prev poll still running
    pollingRef.current = true;
    try {
      const params = showArchived ? "?include_archived=true" : "";
      const res = await axios.get(`${API}/sms/conversations${params}`, getAuth());
      const fresh = res.data || [];

      const currentActive = activeConvIdRef.current;
      const highlights = new Set();
      let activeChanged = false;

      fresh.forEach(c => {
        const prev = convTsMapRef.current.get(c.conversation_id);
        if (prev && c.last_message_at !== prev) {
          if (c.conversation_id === currentActive) activeChanged = true;
          else highlights.add(c.conversation_id);
        } else if (!prev) {
          highlights.add(c.conversation_id);
        }
      });

      // Highlight non-active updated conversations
      if (highlights.size > 0) {
        setHighlightedConvIds(p => { const m = new Set(p); highlights.forEach(id => m.add(id)); return m; });
        setTimeout(() => setHighlightedConvIds(p => { const n = new Set(p); highlights.forEach(id => n.delete(id)); return n; }), 3000);
      }

      const newMap = new Map();
      fresh.forEach(c => newMap.set(c.conversation_id, c.last_message_at));
      convTsMapRef.current = newMap;
      setConversations(mergeWithPendingTemp(fresh));

      // If active conversation got new messages, fetch them incrementally
      if (activeChanged && currentActive) {
        await fetchNewMessages(currentActive);
      }
    } catch {} finally { pollingRef.current = false; }
  }, [showArchived, fetchNewMessages, mergeWithPendingTemp]);

  /* ── SEND MESSAGE ── */
  const sendMessage = async () => {
    if (!newMessage.trim() || !activeConvId || !activeConv || sending) return;
    const body = newMessage.trim();
    const now = new Date().toISOString();
    const optId = `opt_${Date.now()}`;

    setNewMessage("");
    setMessages(prev => [...prev, { message_id: optId, body, direction: "outbound", status: "queued", created_at: now, _optimistic: true }]);
    // Move conversation to top optimistically
    setConversations(prev => {
      const updated = prev.map(c => c.conversation_id === activeConvId ? { ...c, last_message: body.slice(0, 100), last_message_at: now } : c);
      const pinned = updated.filter(c => c.is_pinned).sort((a, b) => (b.last_message_at || "").localeCompare(a.last_message_at || ""));
      const unpinned = updated.filter(c => !c.is_pinned).sort((a, b) => (b.last_message_at || "").localeCompare(a.last_message_at || ""));
      return [...pinned, ...unpinned];
    });
    isNearBottomRef.current = true;
    setShowNewMsgBtn(false); setNewMsgCount(0);
    // Scroll to show optimistic message
    requestAnimationFrame(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }));

    setSending(true);
    try {
      // Reply from the EXACT number that originally received this
      // conversation — never from the user's UI-selected default. This
      // matches what the customer expects: they texted/called a specific
      // line, the reply must come from that line.
      const replyFromNumber = activeConv.phone_number || selectedNumber;
      await axios.post(
        `${API}/sms/send`,
        {
          to_number: activeConv.contact_number,
          body,
          from_number: replyFromNumber,
          conversation_id: activeConvId,
        },
        getAuth()
      );
      // Replace optimistic with real data via incremental fetch
      await fetchNewMessages(activeConvId);
      convTsMapRef.current.set(activeConvId, new Date().toISOString());
    } catch (err) {
      setMessages(prev => prev.map(m => m.message_id === optId ? { ...m, _optimistic: false, status: "failed" } : m));
      const detail = err.response?.data?.detail;
      if (detail?.code === "USAGE_LIMIT_REACHED") {
        toast.error(detail.message, {
          duration: 8000,
          action: { label: "Buy More SMS", onClick: () => { window.location.href = "/dashboard/settings?tab=my-plan&focus=usage"; } },
        });
      } else {
        toast.error((typeof detail === "string" ? detail : detail?.message) || "Failed to send");
      }
    } finally { setSending(false); }
  };

  /* ── NEW CONVERSATION + FIRST MSG ── */
  const startNewConversation = (contact) => {
    const digits = contact.phone.replace(/\D/g, "").slice(-10);
    const existing = conversations.find(c => !c._isTemp && (c.contact_number || "").replace(/\D/g, "").slice(-10) === digits);
    if (existing) {
      // A thread with this exact number already exists — open it, keep the draft.
      pendingRecipientRef.current = null;
      setConversations(prev => prev.filter(c => !c._isTemp));
      setActiveConvId(existing.conversation_id);
      setShowChat(true);
      return;
    }
    const tempId = `temp_${Date.now()}`;
    const temp = {
      conversation_id: tempId,
      contact_name: contact.name,
      contact_number: contact.phone,
      contact_type: contact.type,
      contact_id: contact.id,
      contact_phone_label: contact.phoneLabel || null,
      last_message: "",
      last_message_at: new Date().toISOString(),
      unread_count: 0,
      _isTemp: true,
    };
    pendingRecipientRef.current = temp;
    setConversations(prev => [temp, ...prev.filter(c => !c._isTemp)]);
    setActiveConvId(tempId);
    setMessages([]);
    setShowChat(true);
  };

  /* Explicit user cancel — the ONLY way (besides send success / adoption of
     the real thread) that the selected recipient may be cleared. */
  const discardNewConversation = () => {
    pendingRecipientRef.current = null;
    setConversations(prev => prev.filter(c => !c._isTemp));
    setActiveConvId(null);
    setShowChat(false);
    setNewMessage("");
  };

  const sendFirstMessage = async () => {
    if (!newMessage.trim() || sending) return;
    // Send to the STORED recipient — never to whatever the UI happens to
    // currently render. Re-renders cannot change the destination.
    const pending = pendingRecipientRef.current;
    const recipient = (pending && pending.conversation_id === activeConvId) ? pending : activeConv;
    if (!recipient?.contact_number) {
      toast.error("Recipient is no longer selected. Please choose the recipient again.");
      return;
    }
    // Safety: displayed recipient and stored destination must match.
    if (activeConv?.contact_number && activeConv.contact_number !== recipient.contact_number) {
      toast.error("Recipient mismatch detected — message not sent. Please re-select the recipient.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/sms/send`, {
        to_number: recipient.contact_number,
        body: newMessage.trim(),
        from_number: selectedNumber || phoneNumbers[0]?.phone_number,
        contact_name: recipient.contact_name || undefined,
        contact_type: recipient.contact_type || undefined,
        contact_id: recipient.contact_id || undefined,
      }, getAuth());
      setNewMessage("");
      pendingRecipientRef.current = null;
      const res = await axios.get(`${API}/sms/conversations`, getAuth());
      const convs = res.data || [];
      const digits = recipient.contact_number.replace(/\D/g, "").slice(-10);
      const nc = convs.find(c => (c.contact_number || "").replace(/\D/g, "").slice(-10) === digits);
      if (nc) { setConversations(convs); setActiveConvId(nc.conversation_id); await fetchMessages(nc.conversation_id); const m = new Map(); convs.forEach(c => m.set(c.conversation_id, c.last_message_at)); convTsMapRef.current = m; }
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error((typeof detail === "string" ? detail : detail?.message) || "Failed to send");
    }
    finally { setSending(false); }
  };

  const handleSend = () => {
    const pending = pendingRecipientRef.current;
    if (activeConv?._isTemp || (pending && pending.conversation_id === activeConvId)) return sendFirstMessage();
    return sendMessage();
  };

  /* ── ACTIONS ── */
  const handlePin = async (convId) => { try { const res = await axios.patch(`${API}/sms/conversations/${convId}/pin`, {}, getAuth()); toast.success(res.data.is_pinned ? "Pinned" : "Unpinned"); fetchConversations(true); } catch { toast.error("Failed to update"); } };
  const handleArchive = async (convId) => { try { const res = await axios.patch(`${API}/sms/conversations/${convId}/archive`, {}, getAuth()); toast.success(res.data.is_archived ? "Archived" : "Unarchived"); if (convId === activeConvId) { setActiveConvId(null); setShowChat(false); } fetchConversations(true); } catch { toast.error("Failed to update"); } };
  const handleMarkUnread = async (convId) => { try { await axios.patch(`${API}/sms/conversations/${convId}/mark-unread`, {}, getAuth()); toast.success("Marked as unread"); fetchConversations(true); } catch { toast.error("Failed to update"); } };
  const handleRetry = async (messageId) => { try { await axios.post(`${API}/sms/messages/${messageId}/retry`, {}, getAuth()); toast.success("Message resent"); if (activeConvId) fetchNewMessages(activeConvId); } catch (err) { const d = err.response?.data?.detail; toast.error((typeof d === "string" ? d : d?.message) || "Retry failed"); } };
  const handleJobClick = useCallback((jobId) => { window.location.href = `/dashboard/jobs?highlight=${jobId}`; }, []);
  const handleCallContact = () => { if (activeConv?.contact_number) window.open(`tel:${activeConv.contact_number}`); };
  const handleCopyAddress = () => { const job = convJobs[0]; if (job?.customer_address) { navigator.clipboard.writeText(job.customer_address); toast.success("Address copied"); } };
  const loadOlder = useCallback(() => { if (loadingOlder || !hasMore || !oldestCursor || !activeConvId) return; fetchMessages(activeConvId, oldestCursor); }, [loadingOlder, hasMore, oldestCursor, activeConvId, fetchMessages]);
  const selectConv = (conv) => { setActiveConvId(conv.conversation_id); setShowChat(true); setHighlightedConvIds(p => { const n = new Set(p); n.delete(conv.conversation_id); return n; }); };
  const openSearchResult = (r) => {
    if (r.is_archived && !showArchived) setShowArchived(true);
    if (r.message?.message_id) {
      jumpTargetRef.current = { convId: r.conversation_id, messageId: r.message.message_id, attempts: 0 };
      isNearBottomRef.current = false;
      setJumpTick(t => t + 1);
    }
    selectConv({ conversation_id: r.conversation_id });
  };

  /* ── EFFECTS ── */
  useEffect(() => { fetchConversations(); fetchPhoneNumbers(); }, [fetchConversations, fetchPhoneNumbers]);
  useEffect(() => {
    let f = conversations;
    if (showArchived) f = f.filter(c => c.is_archived);
    else if (filterType === "thumbtack") f = [];
    else if (filterType !== "all") f = f.filter(c => c.contact_type === filterType);
    if (unreadOnly && !showArchived) f = f.filter(c => (c.unread_count || 0) > 0);
    // Thumbtack conversations join the list under "All" and their own filter.
    let tt = (!showArchived && (filterType === "all" || filterType === "thumbtack")) ? ttConvs : [];
    if (unreadOnly) tt = tt.filter(c => (c.unread_count || 0) > 0);
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      f = f.filter(c => (c.contact_name || "").toLowerCase().includes(q) || (c.contact_number || "").includes(q) || (c.last_message || "").toLowerCase().includes(q));
      tt = tt.filter(c => (c.contact_name || "").toLowerCase().includes(q) || (c.last_message || "").toLowerCase().includes(q) || (c.job_display_id || "").toLowerCase().includes(q));
    }
    if (tt.length > 0) {
      const pinned = f.filter(c => c.is_pinned);
      const rest = [...f.filter(c => !c.is_pinned), ...tt]
        .sort((a, b) => (b.last_message_at || "").localeCompare(a.last_message_at || ""));
      setFilteredConvs([...pinned, ...rest]);
    } else {
      setFilteredConvs(f);
    }
  }, [conversations, ttConvs, searchQuery, filterType, showArchived, unreadOnly]);

  // Debounced server-side search inside message bodies
  useEffect(() => {
    const q = searchQuery.trim();
    if (q.length < 2) { setMsgResults([]); setMsgSearching(false); return; }
    setMsgSearching(true);
    const seq = ++msgSearchSeqRef.current;
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/sms/search?q=${encodeURIComponent(q)}`, getAuth());
        if (seq === msgSearchSeqRef.current) setMsgResults(res.data?.results || []);
      } catch { if (seq === msgSearchSeqRef.current) setMsgResults([]); }
      if (seq === msgSearchSeqRef.current) setMsgSearching(false);
    }, 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // Load messages when conversation changes
  useEffect(() => {
    if (!activeConvId) return;
    if (activeConvId.startsWith("tt_")) return; // Thumbtack pane manages its own thread — never hits SMS endpoints
    if (activeConv?._isTemp) {
      // Fresh (not yet sent) conversation — nothing on the server to load.
      setMessages([]); latestMsgTsRef.current = null; setConvJobs([]);
      setShowNewMsgBtn(false); setNewMsgCount(0);
      return;
    }
    setMessages([]); latestMsgTsRef.current = null;
    fetchMessages(activeConvId);
    fetchConvJobs(activeConvId);
    setShowNewMsgBtn(false); setNewMsgCount(0);
  }, [activeConvId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll to bottom when messages first load or conversation changes
  // (skipped while a jump-to-message target is pending)
  useEffect(() => {
    if (!loadingMessages && messages.length > 0 && !jumpTargetRef.current) {
      isNearBottomRef.current = true;
      requestAnimationFrame(() => bottomRef.current?.scrollIntoView({ behavior: "instant" }));
    }
  }, [loadingMessages, activeConvId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Jump-to-message: once the conversation's messages are in, scroll to the
  // target (loading older pages until found, capped at 12 pages ≈ 600 msgs).
  useEffect(() => {
    const t = jumpTargetRef.current;
    if (!t || t.convId !== activeConvId || loadingMessages || loadingOlder) return;
    if (messages.length === 0) return;
    if (messages.some(m => m.message_id === t.messageId)) {
      jumpTargetRef.current = null;
      setJumpHighlightId(t.messageId);
      requestAnimationFrame(() => {
        const el = scrollRef.current?.querySelector(`[data-msg-id="${t.messageId}"]`);
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
      setTimeout(() => setJumpHighlightId(null), 2600);
    } else if (hasMore && oldestCursor && t.attempts < 12) {
      t.attempts += 1;
      fetchMessages(activeConvId, oldestCursor);
    } else {
      jumpTargetRef.current = null;
      if (t.attempts >= 12) toast.info("That message is far back in history — showing recent messages");
    }
  }, [messages, loadingMessages, loadingOlder, hasMore, oldestCursor, activeConvId, jumpTick, fetchMessages]);

  // Auto-scroll when new messages arrive (only if near bottom)
  const prevMsgCountRef = useRef(0);
  useEffect(() => {
    if (messages.length > prevMsgCountRef.current && isNearBottomRef.current) {
      requestAnimationFrame(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }));
    }
    prevMsgCountRef.current = messages.length;
  }, [messages.length]);

  // Poll every 3 seconds
  useEffect(() => {
    pollRef.current = setInterval(poll, 3000);
    return () => clearInterval(pollRef.current);
  }, [poll]);

  // Listen for push notifications — instantly refresh when new message arrives
  useEffect(() => {
    const cleanup = listenForPushMessages((notification) => {
      if (notification.data?.type === 'new_message') {
        // Sound is played once by the dashboard layout — don't double-play here
        // Trigger immediate poll to get the new message
        poll();
        // If we're in the right conversation, it will auto-update
        // If not, the conversation list will reflect the new message
      }
    });
    return cleanup;
  }, [poll]);

  // Handle scroll: track near-bottom + load older
  const handleScroll = useCallback(() => {
    checkIfNearBottom();
    const el = scrollRef.current;
    if (el && el.scrollTop < 80 && hasMore && !loadingOlder) loadOlder();
  }, [checkIfNearBottom, hasMore, loadingOlder, loadOlder]);

  /* ── build message rows with date separators ── */
  const messageRows = useMemo(() => {
    const rows = [];
    for (let i = 0; i < messages.length; i++) {
      const msg = messages[i];
      const prev = i > 0 ? messages[i - 1] : null;
      if (showDateSep(msg.created_at, prev?.created_at)) rows.push({ type: "date", date: fmtDateSep(msg.created_at), key: `date-${msg.created_at}` });
      rows.push({ type: "msg", msg, key: msg.message_id || `msg-${i}` });
    }
    return rows;
  }, [messages]);

  /* ═══ RENDER ═══ */
  return (
    <TooltipProvider delayDuration={300}>
      <div className="-m-4 lg:-m-8 h-[calc(100vh-4rem)] flex bg-background overflow-hidden" data-testid="messages-page">
        {/* LEFT: conversation list */}
        <div className={`w-full md:w-[340px] lg:w-[380px] flex-shrink-0 border-r border-border/40 flex flex-col bg-background ${showChat ? "hidden md:flex" : "flex"}`}>
          <div className="p-3 border-b border-border/40 flex-shrink-0">
            <div className="flex items-center justify-between mb-2.5">
              <h1 className="text-lg font-bold text-foreground">Messages</h1>
              <div className="flex items-center gap-1">
                <Tooltip><TooltipTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setShowNewConv(true)} data-testid="new-conversation-btn"><Plus className="w-4 h-4" /></Button></TooltipTrigger><TooltipContent>New conversation</TooltipContent></Tooltip>
                <Tooltip><TooltipTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => fetchConversations()} data-testid="refresh-conversations"><RefreshCw className="w-4 h-4" /></Button></TooltipTrigger><TooltipContent>Refresh</TooltipContent></Tooltip>
              </div>
            </div>
            <div className="relative mb-2"><Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" /><Input placeholder="Search name, phone, or message..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-8 h-8 text-sm bg-muted/30 border-border/30" data-testid="conversation-search" /></div>
            <div className="flex gap-1 flex-wrap">
              {[{ key: "all", label: "All" }, { key: "customer", label: "Customers" }, { key: "technician", label: "Technicians" }, ...(ttConvs.length > 0 ? [{ key: "thumbtack", label: "Thumbtack" }] : [])].map(t => <button key={t.key} onClick={() => { setFilterType(t.key); setShowArchived(false); }} className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors ${filterType === t.key && !showArchived ? (t.key === "thumbtack" ? "bg-[#009fd9] text-white" : "bg-primary text-primary-foreground") : "bg-muted/40 text-muted-foreground hover:bg-muted/60"}`} data-testid={`filter-${t.key}`}>{t.label}</button>)}
              <button onClick={() => setUnreadOnly(v => !v)} className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors ${unreadOnly ? "bg-primary text-primary-foreground" : "bg-muted/40 text-muted-foreground hover:bg-muted/60"}`} data-testid="filter-unread">Unread</button>
              <button onClick={() => setShowArchived(!showArchived)} className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors flex items-center gap-1 ${showArchived ? "bg-primary text-primary-foreground" : "bg-muted/40 text-muted-foreground hover:bg-muted/60"}`} data-testid="filter-archived"><Archive className="w-3 h-3" /> Archived</button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto overscroll-contain min-h-0" style={{ WebkitOverflowScrolling: "touch" }}>
            {loading ? <div className="flex items-center justify-center p-8"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div> : <>
            {isSearchActive && filteredConvs.length > 0 && <p className="px-3 pt-2.5 pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground" data-testid="search-chats-header">Chats</p>}
            {filteredConvs.length === 0 ? (
              isSearchActive ? <p className="px-3 py-3 text-xs text-muted-foreground" data-testid="search-no-chats">No chats match your search</p>
              : showArchived ? <div className="flex flex-col items-center justify-center p-8 text-center"><Archive className="w-8 h-8 text-muted-foreground/40 mb-2" /><p className="text-sm text-muted-foreground">No archived conversations</p></div> : <EmptyInbox />
            ) :
            filteredConvs.map(conv => <ConversationItem key={conv.conversation_id} conv={conv} isActive={conv.conversation_id === activeConvId} isHighlighted={highlightedConvIds.has(conv.conversation_id)} onClick={() => selectConv(conv)} onPin={() => handlePin(conv.conversation_id)} onArchive={() => handleArchive(conv.conversation_id)} onMarkUnread={() => handleMarkUnread(conv.conversation_id)} />)}
            {isSearchActive && (
              <div data-testid="message-search-results">
                <p className="px-3 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2">In Messages {msgSearching && <Loader2 className="w-3 h-3 animate-spin" />}</p>
                {!msgSearching && msgResults.length === 0 ? (
                  <p className="px-3 py-2 text-xs text-muted-foreground" data-testid="msg-search-empty">No messages contain "{searchQuery.trim()}"</p>
                ) : msgResults.map(r => (
                  <MessageSearchResult key={`msg-${r.conversation_id}`} result={r} query={searchQuery.trim()} onClick={() => openSearchResult(r)} />
                ))}
              </div>
            )}
            </>}
          </div>
        </div>

        {/* RIGHT: chat */}
        <div className={`flex-1 flex flex-col min-w-0 bg-background ${showChat ? "flex" : "hidden md:flex"}`}>
          {!activeConvId ? <NoChatSelected /> : isTtActive ? (
            /* Thumbtack channel — entirely separate pane and send path */
            <ThumbtackConversationPane
              conv={activeTtConv}
              onBack={() => { setShowChat(false); setActiveConvId(null); }}
              onJobClick={handleJobClick}
              onChanged={fetchTtConvs}
            />
          ) : (
            <>
              {/* Header */}
              <div className="px-3 py-2 flex items-center gap-3 border-b border-border/40 flex-shrink-0">
                <button onClick={() => { setShowChat(false); setActiveConvId(null); }} className="md:hidden p-1.5 rounded-lg hover:bg-muted/50" data-testid="chat-back-btn"><ArrowLeft className="w-5 h-5" /></button>
                <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${activeConv?.contact_type === "technician" ? "bg-amber-500/15 text-amber-500" : "bg-blue-500/15 text-blue-500"}`}><User className="w-4 h-4" /></div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <p className="text-sm font-semibold text-foreground truncate">{activeConv?.contact_name || (activeConv?.contact_number ? fmtPhoneDisplay(activeConv.contact_number) : "Unknown")}</p>
                    {customerProfileId && (
                      <Tooltip><TooltipTrigger asChild>
                        <button onClick={() => navigate(`/dashboard/customers?open=${customerProfileId}`)} className="p-0.5 rounded text-muted-foreground hover:text-blue-500 transition-colors flex-shrink-0" data-testid="view-customer-profile-btn">
                          <ExternalLink className="w-3.5 h-3.5" />
                        </button>
                      </TooltipTrigger><TooltipContent>View customer profile</TooltipContent></Tooltip>
                    )}
                  </div>
                  <p className="text-[11px] text-muted-foreground truncate">{activeConv?.contact_number}{activeConv?.contact_phone_label && <span className="ml-1 text-muted-foreground/70">({activeConv.contact_phone_label})</span>}{activeConv?.contact_type && <span className={`ml-1.5 ${activeConv.contact_type === "technician" ? "text-amber-500" : "text-blue-500"}`}>{activeConv.contact_type === "technician" ? "Technician" : "Customer"}</span>}{activeConv?._isTemp && <span className="ml-1.5 text-emerald-500">New conversation</span>}</p>
                </div>
                <div className="flex items-center gap-1">
                  <Tooltip><TooltipTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleCallContact} data-testid="quick-call-btn"><PhoneCall className="w-4 h-4" /></Button></TooltipTrigger><TooltipContent>Call contact</TooltipContent></Tooltip>
                  {activeConv?._isTemp ? (
                    <Tooltip><TooltipTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8" onClick={discardNewConversation} data-testid="discard-new-conv-btn"><X className="w-4 h-4" /></Button></TooltipTrigger><TooltipContent>Cancel new conversation</TooltipContent></Tooltip>
                  ) : (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8" data-testid="quick-actions-btn"><MoreVertical className="w-4 h-4" /></Button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-52">
                      <DropdownMenuItem onClick={() => window.location.href = `/dashboard/jobs?new=true&customer_phone=${activeConv?.contact_number}`} data-testid="action-create-job"><Plus className="w-3.5 h-3.5 mr-2" /> Create job for contact</DropdownMenuItem>
                      {convJobs.length > 0 && <DropdownMenuItem onClick={() => handleJobClick(convJobs[0].job_id)} data-testid="action-open-job"><ExternalLink className="w-3.5 h-3.5 mr-2" /> Open latest job</DropdownMenuItem>}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={handleCopyAddress} disabled={!convJobs.length || !convJobs[0]?.customer_address} data-testid="action-copy-address"><Copy className="w-3.5 h-3.5 mr-2" /> Copy address</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handlePin(activeConvId)}>{activeConv?.is_pinned ? <PinOff className="w-3.5 h-3.5 mr-2" /> : <Pin className="w-3.5 h-3.5 mr-2" />}{activeConv?.is_pinned ? "Unpin" : "Pin conversation"}</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleMarkUnread(activeConvId)}><EyeOff className="w-3.5 h-3.5 mr-2" /> Mark as unread</DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => handleArchive(activeConvId)} className="text-muted-foreground"><Archive className="w-3.5 h-3.5 mr-2" /> Archive conversation</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  )}
                </div>
              </div>

              <LinkedJobsPanel jobs={convJobs} onJobClick={handleJobClick} />

              {/* Messages Area — Simple scroll (no virtualizer) */}
              <div className="relative flex-1 min-h-0">
                <div ref={scrollRef} className="absolute inset-0 overflow-y-auto px-3 py-2 overscroll-contain" style={{ WebkitOverflowScrolling: "touch" }} onScroll={handleScroll} data-testid="chat-messages-area">
                  {hasMore && <div className="flex justify-center py-2"><button onClick={loadOlder} disabled={loadingOlder} className="text-xs text-primary hover:text-primary/80 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary/5 hover:bg-primary/10 transition-colors" data-testid="load-older-btn">{loadingOlder ? <Loader2 className="w-3 h-3 animate-spin" /> : <Clock className="w-3 h-3" />}Load older messages</button></div>}
                  {loadingMessages ? <div className="flex items-center justify-center p-8"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div> :
                  messages.length === 0 ? <div className="flex flex-col items-center justify-center h-full text-center py-12"><MessageCircle className="w-10 h-10 text-muted-foreground/30 mb-3" /><p className="text-sm text-muted-foreground">No messages yet</p><p className="text-xs text-muted-foreground/60 mt-1">Send a message to start the conversation</p></div> :
                  messageRows.map(row => row.type === "date"
                    ? <DateSeparator key={row.key} date={row.date} />
                    : <MessageBubble key={row.key} msg={row.msg} onJobClick={handleJobClick} onRetry={handleRetry} highlight={row.msg.message_id === jumpHighlightId} />
                  )}
                  {/* Bottom sentinel — always present for scrollIntoView */}
                  <div ref={bottomRef} className="h-1" />
                </div>

                {showNewMsgBtn && (
                  <button onClick={() => scrollToBottom()} className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-primary text-primary-foreground text-xs font-medium shadow-lg hover:bg-primary/90 transition-all animate-in fade-in slide-in-from-bottom-2" data-testid="new-message-indicator">
                    <ArrowDown className="w-3.5 h-3.5" />{newMsgCount > 1 ? `${newMsgCount} new messages` : "New message"}
                  </button>
                )}
              </div>

              {/* Composer */}
              <div className="border-t border-border/40 flex-shrink-0">
                <div className="px-3 pt-2"><AiReplyChips conversationId={activeConv?._isTemp ? null : activeConvId} onSelect={text => setNewMessage(text)} /></div>
                {phoneNumbers.length > 1 && <div className="px-3 pt-1"><Select value={selectedNumber} onValueChange={setSelectedNumber}><SelectTrigger className="h-7 text-[11px] w-auto border-border/30 bg-muted/20"><Phone className="w-3 h-3 mr-1" /> <SelectValue /></SelectTrigger><SelectContent>{phoneNumbers.map(p => <SelectItem key={p.phone_number} value={p.phone_number}>{p.phone_number} {p.assignment_type === "business" ? "(Business)" : ""}</SelectItem>)}</SelectContent></Select></div>}
                <div className="flex items-end gap-2 p-3 pt-2">
                  <Textarea ref={inputRef} value={newMessage} onChange={e => setNewMessage(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }} placeholder="Type a message..." rows={1} className="flex-1 min-h-[40px] max-h-[120px] resize-none bg-muted/30 border-border/30 text-sm" data-testid="message-input" />
                  <Button onClick={handleSend} disabled={!newMessage.trim() || sending} size="icon" className="h-10 w-10 rounded-xl flex-shrink-0" data-testid="send-message-btn">{sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}</Button>
                </div>
              </div>
            </>
          )}
        </div>
        <NewConversationDialog open={showNewConv} onClose={setShowNewConv} onSelect={startNewConversation} />
      </div>
    </TooltipProvider>
  );
};

export default MessagesPage;
